import {DomainSection, IntersectionalFilter, ProblemDestination, RecommendationContext} from '../state/global.types';
import {FilterTag} from '@/views/amex/app-types';
import {
  UserProfile,
  Price,
  PlanObjectiveEnum,
  UserActivityCategoryEnum,
  UserType,
} from '@mobi-components/global-solution';
import {adjustToDestinationTimezone} from '../state/fix-solution-dates';
import {TravelersIndormation} from './submit-amex-problem';

export interface ProblemEndpoints {
  origin: ProblemDestination;
  destination: ProblemDestination;
  selectedTags: FilterTag[];
}
export interface ShortProblemDestination {
  departure_timestamp?: number;
  arrival_timestamp?: number;
}
export interface AmexProblem {
  id: string;
  origin: ShortProblemDestination;
  destination: ShortProblemDestination;
  flexible_ordering: boolean;
  activities: RecommendationContext[];
  //@ts-ignore
  mode: process.env.VUE_APP_MODE;

  profile: UserProfile;
  budget: null | {
    price: Price[];
  };
}

export function buildProblemDestination(
  origin: ProblemDestination,
  destination: ProblemDestination,
  availableTags: FilterTag[],
  start: number,
  end: number,
): ProblemEndpoints | null {
  const isItDefaultDestination =
    !destination || (destination.id === '' && destination.lat === 0 && destination.lng === 0);
  const selectedTags = availableTags.filter((x) => {
    // if it's default destination we take all positive tags
    if (isItDefaultDestination) {
      return x.value;
    }

    // when destination is set we consider it as a hard filter, and remove all region or country tags
    // if (x.domain === UserActivityCategoryEnum.COUNTRY || x.domain === UserActivityCategoryEnum.REGION) {
    //   return false;
    // }

    return x.value;
  });
  if (origin && destination) {
    const originWithDeparture: ProblemDestination = {
      ...origin,
      departure_timestamp: Math.floor(start / 1000),
    };
    const destinationWithArriival: ProblemDestination = {
      ...destination,
      arrival_timestamp: Math.floor(end / 1000),
    };

    return {
      origin: originWithDeparture,
      destination: destinationWithArriival,
      selectedTags,
    };
  }
  return null;
}

export function buildUserProfile(
  userId: string,
  tags: FilterTag[],
  intersectionalFilters: IntersectionalFiltersDomain,
) {
  const placesTags = tags.filter((x) => x.domainSection === DomainSection.PLACES);
  const typesTags = tags.filter((x) => x.domainSection === DomainSection.TYPES);
  const rest = tags.filter((x) => !x.domainSection);

  const accommodationIntersectionalFilters = intersectionalFilters[UserActivityCategoryEnum.ACCOMMODATION]
    .filter((x) => x.value)
    .map((x) => {
      const numberValue = parseFloat(x.value as string);
      if (numberValue) {
        return `${x.title}: ${numberValue}`;
      }
      return `${x.title}: ${x.value}`;
    });

  const diningIntersectionalFilters = intersectionalFilters[UserActivityCategoryEnum.DINING]
    .filter((x) => x.value)
    .map((x) => {
      const numberValue = parseFloat(x.value as string);
      if (numberValue) {
        return `${x.title}: ${numberValue}`;
      }
      return `${x.title}: ${x.value}`;
    });
  const exploreIntersectionalFilters = intersectionalFilters[UserActivityCategoryEnum.EXPERIENCE]
    .filter((x) => x.value)
    .map((x) => {
      const numberValue = parseFloat(x.value as string);
      if (numberValue) {
        return `${x.title}: ${numberValue}`;
      }
      return `${x.title}: ${x.value}`;
    });

  return {
    user_id: userId,
    user_type: UserType.LEISURE_TRAVELLER,
    name: '',
    avatar: '',
    optional_needs: [],
    available_modes: [],
    mode_preferences: [],
    price_tolerance: null,
    preferences: [
      {
        category: UserActivityCategoryEnum.ACCOMMODATION,
        included: true,
        plan_objective: PlanObjectiveEnum.EXPLORATION,
        places: [...placesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.ACCOMMODATION)
          .map((x) => x.name),
        types: [...typesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.ACCOMMODATION)
          .map((x) => x.name)
          .concat(accommodationIntersectionalFilters),
        minimum_rating: 0,
        selection: null,
      },
      {
        category: UserActivityCategoryEnum.DINING,
        included: true,
        plan_objective: PlanObjectiveEnum.EXPLORATION,
        places: [...placesTags, ...rest].filter((x) => x.domain === UserActivityCategoryEnum.DINING).map((x) => x.name),
        types: [...typesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.DINING)
          .map((x) => x.name)
          .concat(diningIntersectionalFilters),
        minimum_rating: 0,
        selection: null,
      },
      {
        category: UserActivityCategoryEnum.EXPERIENCE,
        included: true,
        plan_objective: PlanObjectiveEnum.EXPLORATION,
        places: [...placesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.EXPERIENCE)
          .map((x) => x.name),
        types: [...typesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.EXPERIENCE)
          .map((x) => x.name)
          .concat(exploreIntersectionalFilters),
        minimum_rating: 0,
        selection: null,
      },
      {
        category: UserActivityCategoryEnum.GEOLOCATION,
        included: true,
        plan_objective: PlanObjectiveEnum.NEEDS,
        places: [...placesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.GEOLOCATION)
          .map((x) => x.name),
        types: [...typesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.GEOLOCATION)
          .map((x) => x.name),
        minimum_rating: 0,
        selection: null,
      },
      {
        category: UserActivityCategoryEnum.TRANSPORTATION,
        included: true,
        plan_objective: PlanObjectiveEnum.NEEDS,
        places: [...placesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.TRANSPORTATION)
          .map((x) => x.name),
        types: [...typesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.TRANSPORTATION)
          .map((x) => x.name),
        minimum_rating: 0,
        selection: null,
      },
      {
        category: UserActivityCategoryEnum.TRAVELER,
        included: true,
        plan_objective: PlanObjectiveEnum.EXPLORATION,
        places: [],
        types: [],
        minimum_rating: 0,
        selection: null,
      },
      {
        category: UserActivityCategoryEnum.TIMEFRAME,
        included: true,
        plan_objective: PlanObjectiveEnum.EXPLORATION,
        places: [...placesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.TIMEFRAME)
          .map((x) => x.name),
        types: [...typesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.TIMEFRAME)
          .map((x) => x.name),
        minimum_rating: 0,
        selection: null,
      },
      {
        category: UserActivityCategoryEnum.TRIPPREFERENCE,
        included: true,
        plan_objective: PlanObjectiveEnum.EXPLORATION,
        places: [...placesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.TRIPPREFERENCE)
          .map((x) => x.name),
        types: [...typesTags, ...rest]
          .filter((x) => x.domain === UserActivityCategoryEnum.TRIPPREFERENCE)
          .map((x) => x.name),
        minimum_rating: 0,
        selection: null,
      },
    ],
  };
}

export interface IntersectionalFiltersDomain {
  [UserActivityCategoryEnum.ACCOMMODATION]: IntersectionalFilter[];
  [UserActivityCategoryEnum.DINING]: IntersectionalFilter[];
  [UserActivityCategoryEnum.EXPERIENCE]: IntersectionalFilter[];
}

export function buildProblem(
  id: string,
  userId: string,
  origin: ProblemDestination,
  destination: ProblemDestination,
  tags: FilterTag[],
  budget: Price[] = [],
  travelersInformation: TravelersIndormation,
  intersectionalFilters: IntersectionalFiltersDomain,
): AmexProblem {
  const originDepartureMs =
    origin.time_zone && origin.departure_timestamp
      ? adjustToDestinationTimezone(origin.departure_timestamp * 1000, origin.time_zone)
      : (origin.departure_timestamp || 0) * 1000;
  const destArrivalMs =
    destination.time_zone && destination.arrival_timestamp
      ? adjustToDestinationTimezone(destination.arrival_timestamp * 1000, destination.time_zone)
      : (destination.arrival_timestamp || 0) * 1000;
  const originDepartureSNoon = originDepartureMs ? Math.round((originDepartureMs + 12 * 3600 * 1000) / 1000) : 0;
  const destArrivalSNoon = destArrivalMs ? Math.round((destArrivalMs + 12 * 3600 * 1000) / 1000) : 0;

  const fullOrigin = origin.name
    ? {...origin, departure_timestamp: originDepartureSNoon}
    : {departure_timestamp: originDepartureSNoon};
  const fullDestination = destination.name
    ? {...destination, arrival_timestamp: destArrivalSNoon}
    : {arrival_timestamp: destArrivalSNoon};

  return {
    id,
    origin: fullOrigin,
    destination: fullDestination,
    flexible_ordering: true,
    activities: [],
    //@ts-ignore
    mode: process.env.VUE_APP_MODE,
    budget: {price: budget},
    profile: buildUserProfile(userId, tags, intersectionalFilters),
    //@ts-ignore
    traveler_information: travelersInformation,
  };
}
